import { useSession } from '@/hooks/use-session';
import { useCalendarAlertsData, useMarketAlertsData, useNewsAlertsData } from './use-alerts-data';
import { CalendarAlert, MarketAlert, NewsAlert } from '@/utils/api';
import { useState, useEffect } from 'react';

interface UseAlertsResult {
  calendarAlerts: CalendarAlert[];
  marketAlerts: MarketAlert[];
  newsAlerts: NewsAlert[];
  isLoadingCalendar: boolean;
  isLoadingMarket: boolean;
  isLoadingNews: boolean;
  errorCalendar: string | null;
  errorMarket: string | null;
  errorNews: string | null;
  refetchCalendar: () => Promise<void>;
  refetchMarket: () => Promise<void>;
  refetchNews: () => Promise<void>;
}

export function useAlerts(limit = 50): UseAlertsResult {
  const { session } = useSession();
  
  // Stagger the API calls to prevent concurrent overload
  const [enableCalendar, setEnableCalendar] = useState(false);
  const [enableNews, setEnableNews] = useState(false);
  
  useEffect(() => {
    // Calendar alerts after 100ms
    const calendarTimer = setTimeout(() => setEnableCalendar(true), 100);
    // News alerts after 200ms  
    const newsTimer = setTimeout(() => setEnableNews(true), 200);
    
    return () => {
      clearTimeout(calendarTimer);
      clearTimeout(newsTimer);
    };
  }, []);
  
  // Market alerts start immediately
  const { 
    data: marketAlerts = [], 
    isLoading: isLoadingMarket, 
    error: errorMarket,
    refetch: refetchMarket
  } = useMarketAlertsData(session, {
    enabled: !!session?.user?.id
  });
  
  // Calendar alerts start after 100ms delay
  const { 
    data: calendarAlerts = [], 
    isLoading: isLoadingCalendar, 
    error: errorCalendar,
    refetch: refetchCalendar
  } = useCalendarAlertsData(session, {
    enabled: !!session?.user?.id && enableCalendar
  });

  // News alerts start after 200ms delay
  const { 
    data: newsAlerts = [], 
    isLoading: isLoadingNews, 
    error: errorNews,
    refetch: refetchNews
  } = useNewsAlertsData(session, {
    enabled: !!session?.user?.id && enableNews
  });

  // Limit alerts to the most recent limit to improve performance
  const limitedCalendarAlerts = calendarAlerts.slice(0, limit);
  const limitedMarketAlerts = marketAlerts.slice(0, limit);
  const limitedNewsAlerts = newsAlerts.slice(0, limit);

  return {
    calendarAlerts: limitedCalendarAlerts,
    marketAlerts: limitedMarketAlerts,
    newsAlerts: limitedNewsAlerts,
    isLoadingCalendar,
    isLoadingMarket,
    isLoadingNews,
    errorCalendar: errorCalendar ? (errorCalendar as Error).message : null,
    errorMarket: errorMarket ? (errorMarket as Error).message : null,
    errorNews: errorNews ? (errorNews as Error).message : null,
    refetchCalendar: async () => {
      await refetchCalendar();
    },
    refetchMarket: async () => {
      await refetchMarket();
    },
    refetchNews: async () => {
      await refetchNews();
    }
  };
} 